import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetOrderServicesLookupList } from '@/features/orders/types/orders-dto';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

const LOOKUP_PAGE_SIZE = 200;

type Props = {
    search?: string;
} & ApiHookProps;

export const useOrderServicesLookupApi = ({ search }: Props = {}) => {
    const axiosInstance = useAxiosAuth();

    const {
        data: orderServicesLookupList, 
        isLoading: isLoadingOrderServicesLookupList, 
        isError: isErrorOrderServicesLookupList,
        fetchNextPage: fetchOrderServicesLookupListNextPage,
        hasNextPage: hasOrderServicesLookupListNextPage,
        isFetchingNextPage: isFetchingOrderServicesLookupListNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.orders.queryKeys.orderServicesLookup, 
            LOOKUP_PAGE_SIZE,
            search,
        ],
        queryFn: async ({pageParam}): Promise<GetOrderServicesLookupList> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.orders.endpoints.orderServicesLookup, {    
                take: LOOKUP_PAGE_SIZE,
                skip: pageParam,
                search: search?.trim() || undefined,
            });
            const response = await axiosInstance.get<GetOrderServicesLookupList>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: 0,
    });

    const fetchedOrderServicesLookupList = orderServicesLookupList?.pages.flatMap((page) => page.items ?? []);

    return {
        orderServicesLookupList: fetchedOrderServicesLookupList, 
        isLoadingOrderServicesLookupList, 
        isErrorOrderServicesLookupList,
        fetchOrderServicesLookupListNextPage,
        hasOrderServicesLookupListNextPage,
        isFetchingOrderServicesLookupListNextPage,
    }
};
